package com.tovtalk.ui.screen.story

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.repository.PostRepository
import com.tovtalk.ui.components.TovTalkAsyncImage
import com.tovtalk.ui.components.TovTalkReelPlayer
import kotlinx.coroutines.delay

private const val STORY_IMAGE_DURATION_MS = 5000L

@Composable
fun StoryViewerScreen(
    userId: Int,
    postRepository: PostRepository,
    onClose: () -> Unit,
) {
    var stories by remember { mutableStateOf<List<Post>>(emptyList()) }
    var index by remember { mutableStateOf(0) }
    var loading by remember { mutableStateOf(true) }

    LaunchedEffect(userId) {
        when (val result = postRepository.getUserStories(userId)) {
            is DomainResult.Success -> {
                if (result.data.isEmpty()) {
                    onClose()
                } else {
                    stories = result.data
                    loading = false
                }
            }
            is DomainResult.Error -> {
                loading = false
                onClose()
            }
            DomainResult.Loading -> {
                // getUserStories does not emit Loading; no-op to satisfy exhaustiveness
            }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black)
            .pointerInput(stories.size) {
                detectTapGestures(
                    onTap = { offset ->
                        if (offset.x < size.width / 2f) {
                            if (index > 0) index-- else onClose()
                        } else {
                            if (index < stories.lastIndex) index++ else onClose()
                        }
                    }
                )
            }
    ) {
        val current = stories.getOrNull(index)
        when {
            current != null -> {
                fun advance() {
                    if (index < stories.lastIndex) index++ else onClose()
                }
                // Render video or image depending on whether a video URL is available.
                // key(current.id) resets the player per story so previous audio/playback
                // does not leak into the next story.
                if (current.videoUrl.isNotBlank()) {
                    key(current.id) {
                        TovTalkReelPlayer(
                            url = current.videoUrl,
                            modifier = Modifier.fillMaxSize(),
                            isPlaying = true,
                            thumbnailUrl = current.thumbnail,
                            fillContainer = true,
                            // Prefetch next story video if available
                            nextVideoUrl = stories.getOrNull(index + 1)?.videoUrl?.takeIf { it.isNotBlank() },
                            // Auto-advance when the video finishes
                            onVideoEnded = { advance() },
                        )
                    }
                } else {
                    // Image-type story (no video URL): show image.
                    // Note: Post has no dedicated full-res image field; thumbnail is the
                    // only image source available for image-type stories.
                    val imageUrl = current.thumbnail ?: ""
                    key(current.id) {
                        TovTalkAsyncImage(
                            url = imageUrl,
                            modifier = Modifier.fillMaxSize(),
                            clipToCircle = false,
                            contentScale = ContentScale.Fit,
                        )
                    }
                    // Auto-advance image stories after a fixed duration
                    LaunchedEffect(index) {
                        delay(STORY_IMAGE_DURATION_MS)
                        advance()
                    }
                }

                StoryProgressBars(
                    count = stories.size,
                    activeIndex = index,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 8.dp, vertical = 12.dp)
                        .align(Alignment.TopCenter),
                )
            }
            loading -> {
                CircularProgressIndicator(
                    color = Color.White,
                    modifier = Modifier.align(Alignment.Center),
                )
            }
            else -> {}
        }
    }
}

@Composable
fun StoryProgressBars(
    count: Int,
    activeIndex: Int,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        repeat(count) { i ->
            Box(
                modifier = Modifier
                    .weight(1f)
                    .height(3.dp)
                    .background(
                        if (i <= activeIndex) Color.White else Color.White.copy(alpha = 0.4f)
                    )
            )
        }
    }
}
